import CloseOutlined from '@mui/icons-material/CloseOutlined';
import IconButton from '@mui/material/IconButton';
import Link from '@mui/material/Link';
import Snackbar from '@mui/material/Snackbar';
import Stack from '@mui/material/Stack';
import SvgIcon from '@mui/material/SvgIcon';
import Typography from '@mui/material/Typography';
import type { ReactNode } from 'react';

const SUCCESS_BORDER = '#77D3A7';
export const SAVED_TO_OUTCOMES = "It's saved to your Outcomes for you to view it here now.";
const TOAST_RADIUS = 9;

function SuccessCheck(): ReactNode {
  return (
    <SvgIcon viewBox="0 0 28 28" sx={{ fontSize: 26, flexShrink: 0 }}>
      <rect x="0.5" y="0.5" width="27" height="27" rx="13.5" fill="#BBE9D3" stroke="#2C9C6D" />
      <path
        fill="#2C9C6D"
        d="M20.5306 11.0308L12.5306 19.0308C12.4609 19.1007 12.3781 19.1562 12.287 19.1941C12.1958 19.2319 12.0981 19.2514 11.9993 19.2514C11.9006 19.2514 11.8029 19.2319 11.7117 19.1941C11.6206 19.1562 11.5378 19.1007 11.4681 19.0308L7.9681 15.5308C7.89833 15.461 7.84299 15.3782 7.80524 15.2871C7.76748 15.1959 7.74805 15.0982 7.74805 14.9996C7.74805 14.9009 7.76748 14.8032 7.80524 14.7121C7.84299 14.6209 7.89833 14.5381 7.9681 14.4683C8.03786 14.3985 8.12069 14.3432 8.21184 14.3055C8.30299 14.2677 8.40069 14.2483 8.49935 14.2483C8.59801 14.2483 8.69571 14.2677 8.78686 14.3055C8.87801 14.3432 8.96083 14.3985 9.0306 14.4683L12 17.4377L19.4693 9.96956C19.6102 9.82867 19.8013 9.74951 20.0006 9.74951C20.1999 9.74951 20.391 9.82867 20.5318 9.96956C20.6727 10.1105 20.7519 10.3016 20.7519 10.5008C20.7519 10.7001 20.6727 10.8912 20.5318 11.0321L20.5306 11.0308Z"
      />
    </SvgIcon>
  );
}

interface RunCreatedToastProps {
  open: boolean;
  actionName: string;
  outcomeKind: string | null;
  detail?: string | null;
  titleText?: string;
  viewLabel?: string;
  onView?: () => void;
  onClose: () => void;
}

function title(actionName: string, outcomeKind: string | null): string {
  const noun =
    outcomeKind === null ||
    actionName.split(' ').at(-1)?.toLowerCase() === outcomeKind.toLowerCase()
      ? ''
      : ` ${outcomeKind}`;
  return `${actionName}${noun} has been successfully created`;
}

export function RunCreatedToast({
  open,
  actionName,
  outcomeKind,
  detail = null,
  titleText,
  viewLabel = 'Click here',
  onView,
  onClose,
}: RunCreatedToastProps): ReactNode {
  return (
    <Snackbar
      open={open}
      autoHideDuration={6000}
      anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      onClose={onClose}
    >
      <Stack
        role="status"
        direction="row"
        data-testid="run-created-toast"
        sx={{
          alignItems: 'center',
          gap: 3,
          px: 3,
          py: 2,
          borderRadius: `${TOAST_RADIUS}px`,
          border: '1px solid',
          borderColor: SUCCESS_BORDER,
          bgcolor: 'background.paper',
          boxShadow: '0 12px 32px rgba(15, 23, 43, 0.12)',
        }}
      >
        <SuccessCheck />
        <Stack sx={{ gap: 0.25 }}>
          <Typography sx={{ fontSize: 12, fontWeight: 600 }}>
            {titleText ?? title(actionName, outcomeKind)}
          </Typography>
          {detail === null ? null : (
            <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>{detail}</Typography>
          )}
        </Stack>
        {onView === undefined ? null : (
          <Link component="button" type="button" onClick={onView} sx={{ fontSize: 12 }}>
            {viewLabel}
          </Link>
        )}
        <IconButton size="small" aria-label="Dismiss" onClick={onClose}>
          <CloseOutlined sx={{ fontSize: 16 }} />
        </IconButton>
      </Stack>
    </Snackbar>
  );
}
